import { Trans, useLingui } from '@lingui/react/macro';
import { MediaKind } from 'modules/manager/domain/media-kind';
import { MediaSubjectType } from 'modules/manager/domain/media-kind/media-kind-catalog';
import { useChampionshipMediaSubjectsQuery, useGenerateMediaMutation } from 'modules/manager/store/media-generation-jobs-api';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { UDSelect, SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';

interface GenerateMediaModalProps {
  championshipId: number;
  kind: MediaKind;
  subjectType: MediaSubjectType;
  kindTitle: string;
  onClose: () => void;
  onGenerated: () => void;
}

const GenerateMediaModal = ({
  championshipId,
  kind,
  subjectType,
  kindTitle,
  onClose,
  onGenerated,
}: GenerateMediaModalProps) => {
  const { t } = useLingui();
  const { data: subjects, isFetching } = useChampionshipMediaSubjectsQuery(championshipId);
  const [generateMedia, { isLoading }] = useGenerateMediaMutation();
  const [selected, setSelected] = useState<SelectOption<number> | null>(null);

  const options: SelectOption<number>[] = useMemo(() => {
    if (!subjects) return [];
    if (subjectType === 'match') {
      return subjects.matches.map((match) => {
        const home = match.teams[0]?.name ?? '—';
        const away = match.teams[1]?.name ?? '—';
        const round = match.round?.name ? `, ${match.round.name}` : '';
        return { value: match.id, label: `${home} — ${away}${round}` };
      });
    }
    if (subjectType === 'round') {
      return subjects.rounds.map((round) => ({ value: round.id, label: round.name }));
    }
    if (subjectType === 'stage') {
      return subjects.stages.map((stage) => ({ value: stage.id, label: stage.name }));
    }
    if (subjectType === 'group') {
      return subjects.groups.map((group) => ({ value: group.id, label: group.name }));
    }
    return [];
  }, [subjects, subjectType]);

  const handleSubmit = async () => {
    if (!selected) return;
    try {
      await generateMedia({ championshipId, kind, subjectId: selected.value }).unwrap();
      onGenerated();
    } catch (error) {
      console.error('Failed to generate media:', error);
      toast.error(t`Не удалось поставить генерацию в очередь`);
    }
  };

  return (
    <Modal show onClose={onClose} size="md">
      <Modal.Header title={t`Сгенерировать: ${kindTitle}`} />
      <Modal.Body>
        <div data-testid="generate-media-modal">
          {isFetching && !subjects ? (
            <div className="skeleton-block h-12 w-full" />
          ) : options.length === 0 ? (
            <UDText type="subhead" className="text-inkSecondary">
              <Trans>Нет подходящих объектов для этого типа медиа.</Trans>
            </UDText>
          ) : (
            <UDSelect
              placeholder={t`Выберите объект`}
              options={options}
              value={selected}
              onChange={(value) => setSelected(value as SelectOption<number> | null)}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <UDButton variant="secondary" type="button" onClick={onClose} disabled={isLoading}>
          <Trans>Отмена</Trans>
        </UDButton>
        <UDButton
          variant="primary"
          type="button"
          onClick={() => void handleSubmit()}
          loading={isLoading}
          disabled={!selected}
        >
          <Trans>Сгенерировать</Trans>
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateMediaModal;
